import { Api, Article, Key } from '@mui/icons-material';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { TrcApp, useLoader } from 'thermia-react-container';
import useApiClient from './api/api-client';
import { LockedError } from './api/api-types';
import { setConfig } from './config';
import DocsPage from './pages/docs/docs-page';
import ExplorePage from './pages/explore/explore-page';
import AddKeyPage from './pages/keys/add-key-page';
import KeysPage from './pages/keys/keys-page';
import Landing from './pages/landing/landing';

const App = () => {
  useEffect(() => {
    document.title = 'Thermia Developer Portal';
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/*" element={<LoggedInApp />} />
      </Routes>
    </BrowserRouter>
  );
};

const ShowContentContext = createContext<(show: boolean) => void>(() => {});

const ApplicationProvider: FC<PropsWithChildren> = ({ children }) => {
  const { usersMe } = useApiClient();
  const { Loader } = useLoader();
  const [isLoading, setIsLoading] = useState(true);
  const userHasAccess = useRef(false);
  const hasInitialized = useRef(false);
  const setShowContent = useContext(ShowContentContext);

  useEffect(() => {
    const initialize = async () => {
      try {
        await usersMe();
        userHasAccess.current = true;
        setShowContent(true);
      } catch (error) {
        if (error instanceof LockedError) {
          userHasAccess.current = false;
          setShowContent(false);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (!hasInitialized.current) {
      hasInitialized.current = true;
      initialize();
    }
  }, [usersMe, setShowContent]);

  if (isLoading) return <Loader />;

  return userHasAccess.current ? <>{children}</> : <></>;
};

const LoggedInApp = () => {
  const { t } = useTranslation('portal');
  const [showContent, setShowContent] = useState(false);

  return (
    <ShowContentContext.Provider value={setShowContent}>
      <TrcApp
        configPath="/config/config.json"
        setConfig={setConfig}
        title={showContent ? 'Thermia Developer Portal' : ''}
        contentMaxWidth={'xl'}
        applicationProvider={ApplicationProvider}
        routes={
          <>
            <Route path="/login" element={<Navigate to={`/docs`} replace />} />
            <Route path="/docs/*" element={<DocsPage />} />
            <Route path="/keys" element={<KeysPage />} />
            <Route path="/add-key" element={<AddKeyPage />} />
            <Route path="/explore/*" element={<ExplorePage />} />
          </>
        }
        tabs={
          showContent
            ? [
                { path: 'docs', icon: Article, label: t('portal.menu.docs') },
                { path: 'keys', icon: Key, label: t('portal.menu.keys') },
                { path: 'explore', icon: Api, label: t('portal.menu.explore') }
              ]
            : []
        }
      />
    </ShowContentContext.Provider>
  );
};

export default App;
